import './styles.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast as Toast } from 'react-toastify';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Collapse,
  Modal,
  Progress,
  Spinner,
  Table,
} from 'reactstrap';

import {
  cancelCigarEmbeddingJob,
  type EmbeddingJob,
  type EmbeddingJobActivityItem,
  type EmbeddingJobError,
  type EmbeddingJobMode,
  getCigarEmbeddingJob,
  isEmbeddingJobActive,
  listCigarEmbeddingJobs,
  retryCigarEmbeddingJobFailures,
  startCigarEmbeddingJob,
  type StartEmbeddingJobRequest,
} from '@/api/cigar-matching';
import Icon from '@/components/icon';
import ModalDialog from '@/components/modal-dialog';
import ToastContent from '@/components/toasts/content';

import {
  formatEmbeddingProcessingTime,
  formatEmbeddingRelativeTime,
  getEmbeddingActivityStatusLabel,
  getEmbeddingFailureLabel,
  getEmbeddingPreparationPresentation,
  isEmbeddingActivityStale,
} from './embedding-display-utils';
import EmbeddingRuntimeLogsPanel from './embedding-runtime-logs';
import { useEmbeddingJobPolling } from './use-embedding-job-polling';

const RECENT_JOB_LIMIT = 20;
const RETRYABLE_JOB_STATUSES = new Set(['completed_with_errors', 'failed']);

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const humanizeKey = (value) => {
  if (!value || typeof value !== 'string') return 'Unknown';
  return value.split('_').join(' ');
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatDuration = (milliseconds) => {
  if (!Number.isFinite(Number(milliseconds))) return '—';
  const seconds = Math.max(0, Math.round(Number(milliseconds) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

const getElapsedMilliseconds = (job: EmbeddingJob) => {
  if (!job.started_at) return null;
  const started = new Date(job.started_at).getTime();
  const ended = job.completed_at
    ? new Date(job.completed_at).getTime()
    : Date.now();
  if (!Number.isFinite(started) || !Number.isFinite(ended)) return null;
  return Math.max(0, ended - started);
};

const isSafeHttpUrl = (value) => {
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

const upsertJob = (jobs: EmbeddingJob[], job: EmbeddingJob) =>
  [job, ...jobs.filter((candidate) => candidate.id !== job.id)].slice(
    0,
    RECENT_JOB_LIMIT
  );

const getJobUpdatedAt = (job: EmbeddingJob) => {
  const updatedAt = new Date(job.updated_at).getTime();
  return Number.isFinite(updatedAt) ? updatedAt : 0;
};

export const canRetryEmbeddingJob = (job?: EmbeddingJob | null) =>
  Boolean(
    job && RETRYABLE_JOB_STATUSES.has(job.status) && Number(job.failed) > 0
  );

export const canCancelEmbeddingJob = (job?: EmbeddingJob | null) =>
  isEmbeddingJobActive(job);

export const getEmbeddingJobFailureContext = (failureCode?: string | null) => {
  if (!failureCode) return 'Job failure';
  return getEmbeddingFailureLabel(failureCode);
};

export const getEmbeddingJobProgress = (job?: EmbeddingJob | null) => {
  if (!job || job.dry_run || job.discovered <= 0) return null;
  return Math.min(100, Math.max(0, (job.processed / job.discovered) * 100));
};

export const getEmbeddingJobProgressText = (job: EmbeddingJob) => {
  if (job.dry_run) return `${job.discovered.toLocaleString()} found`;
  const progress = getEmbeddingJobProgress(job);
  if (progress === null) return '—';
  return `${job.processed.toLocaleString()} / ${job.discovered.toLocaleString()} (${progress.toFixed(1)}%)`;
};

export const getEmbeddingJobStatusLabel = (status) => {
  const labels = {
    queued: 'Queued',
    discovering: 'Discovering eligible images',
    running: 'Generating embeddings',
    completed: 'Completed',
    completed_with_errors: 'Completed with errors',
    cancelled: 'Cancelled',
    failed: 'Failed',
  };
  return labels[status] || humanizeKey(status);
};

const getStatusColor = (status) => {
  const colors = {
    queued: 'secondary',
    discovering: 'info',
    running: 'primary',
    completed: 'success',
    completed_with_errors: 'warning',
    cancelled: 'secondary',
    failed: 'danger',
  };
  return colors[status] || 'secondary';
};

const getModeLabel = (mode: EmbeddingJobMode) =>
  mode === 'regenerate_all' ? 'Full regeneration' : 'Generate missing';

const MetadataCounts = ({ title, counts }) => {
  const entries = Object.entries(counts || {});
  if (!entries.length) return null;

  return (
    <div className="recognition-job-metadata-group">
      <small>{title}</small>
      <div>
        {entries.map(([key, count]) => (
          <span key={key}>
            {humanizeKey(key)} <strong>{Number(count).toLocaleString()}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

const getActivityStatusColor = (status) => {
  const colors = {
    pending: 'secondary',
    processing: 'primary',
    embedded: 'success',
    skipped: 'info',
    failed: 'danger',
  };
  return colors[status] || 'secondary';
};

const getActivitySourceLabel = (item: EmbeddingJobActivityItem) =>
  item.source_entity_type === 'vitola' ? 'vitola image' : 'catalog image';

const ActivityThumbnail = ({ item }: { item: EmbeddingJobActivityItem }) => {
  if (!isSafeHttpUrl(item.image_url)) return null;
  return (
    <a
      className="recognition-activity-thumbnail"
      href={item.image_url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open source image for cigar ${item.cigar_id}`}
    >
      <img
        src={item.image_url}
        alt=""
        onError={(event) => {
          const thumbnail = event.currentTarget.parentElement;
          if (thumbnail) thumbnail.style.display = 'none';
        }}
      />
    </a>
  );
};

export const EmbeddingJobActivityPanel = ({ job }: { job: EmbeddingJob }) => {
  const { activity } = job;
  if (!activity) return null;

  const remaining = Math.max(0, Number(activity.remaining) || 0);
  const active = isEmbeddingJobActive(job);
  const stale = active && isEmbeddingActivityStale(activity.last_activity_at);
  const recentItems = [...(activity.recent_items || [])].sort(
    (left, right) =>
      new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime()
  );

  return (
    <section className="recognition-job-activity">
      <div className="recognition-job-activity-heading">
        <div>
          <strong>Recent activity</strong>
          <span>
            Last activity:{' '}
            {formatEmbeddingRelativeTime(activity.last_activity_at)}
          </span>
        </div>
        {stale && <Badge color="warning">No recent progress</Badge>}
        {!stale && active && activity.active && (
          <Badge color="success">Active</Badge>
        )}
      </div>

      {job.status === 'cancelled' && remaining > 0 ? (
        <div className="recognition-activity-remaining cancelled">
          Cancelled with {remaining.toLocaleString()} unprocessed
        </div>
      ) : (
        active && (
          <div className="recognition-activity-remaining">
            {remaining.toLocaleString()} remaining
          </div>
        )
      )}

      {recentItems.length > 0 && (
        <div className="recognition-activity-list">
          {recentItems.map((item) => {
            const preparation = getEmbeddingPreparationPresentation(
              item.preparation_outcome
            );
            const processingTime = formatEmbeddingProcessingTime(
              item.processing_ms
            );
            const statusLabel =
              job.status === 'cancelled' && item.status === 'pending'
                ? 'Unprocessed'
                : getEmbeddingActivityStatusLabel(item.status);
            const failureMessage =
              item.status === 'failed'
                ? item.error_detail || getEmbeddingFailureLabel(item.error_code)
                : null;

            return (
              <article key={item.id} data-status={item.status}>
                <ActivityThumbnail item={item} />
                <div className="recognition-activity-content">
                  <div className="recognition-activity-event">
                    <Badge color={getActivityStatusColor(item.status)}>
                      {statusLabel}
                    </Badge>
                    <span>{getActivitySourceLabel(item)} for cigar</span>
                    <a
                      href={`/cigars/manager?id=${item.cigar_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.cigar_id}
                    </a>
                  </div>
                  {failureMessage && (
                    <span className="recognition-activity-error">
                      {failureMessage}
                    </span>
                  )}
                  <div className="recognition-activity-meta">
                    {processingTime && <span>{processingTime}</span>}
                    {preparation && (
                      <span title={preparation.description}>
                        {preparation.label}
                      </span>
                    )}
                    {item.attempt_count > 0 && (
                      <span>Attempt {item.attempt_count}</span>
                    )}
                    <span>{formatEmbeddingRelativeTime(item.updated_at)}</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export const EmbeddingJobProgress = ({ job }: { job: EmbeddingJob }) => {
  const progress = getEmbeddingJobProgress(job);
  const elapsed = getElapsedMilliseconds(job);

  return (
    <div className="recognition-current-job" data-status={job.status}>
      <div className="recognition-current-job-heading">
        <div>
          <div className="recognition-job-badges">
            <Badge color={getStatusColor(job.status)}>
              {getEmbeddingJobStatusLabel(job.status)}
            </Badge>
            <Badge color="info">{getModeLabel(job.mode)}</Badge>
            {job.dry_run && <Badge color="info">Discovery only</Badge>}
            {job.item_limit !== null && (
              <Badge color="info">Limit {job.item_limit}</Badge>
            )}
          </div>
          <small>Job {job.id}</small>
          {job.completed_at && (
            <small className="d-block text-muted">
              Completed {formatDateTime(job.completed_at)}
            </small>
          )}
        </div>
        {isEmbeddingJobActive(job) && (
          <Spinner color="primary" size="sm" aria-label="Job in progress" />
        )}
      </div>

      {job.dry_run ? (
        <div className="recognition-dry-run-result">
          <strong>{job.discovered.toLocaleString()}</strong>
          <span>eligible catalog sources discovered</span>
        </div>
      ) : (
        <div className="recognition-job-progress-block">
          <div>
            <span>
              {job.discovered > 0
                ? `${job.processed.toLocaleString()} / ${job.discovered.toLocaleString()} processed`
                : getEmbeddingJobStatusLabel(job.status)}
            </span>
            {progress !== null && <strong>{progress.toFixed(1)}%</strong>}
          </div>
          {progress !== null && <Progress value={progress} />}
        </div>
      )}

      <div className="recognition-job-stats">
        <div>
          <small>Discovered</small>
          <strong>{job.discovered.toLocaleString()}</strong>
        </div>
        <div>
          <small>Embedded</small>
          <strong>{job.embedded.toLocaleString()}</strong>
        </div>
        <div>
          <small>Skipped</small>
          <strong>{job.skipped.toLocaleString()}</strong>
        </div>
        <div className={job.failed ? 'failed' : undefined}>
          <small>Failed</small>
          <strong>{job.failed.toLocaleString()}</strong>
        </div>
        <div>
          <small>Elapsed</small>
          <strong>{elapsed === null ? '—' : formatDuration(elapsed)}</strong>
        </div>
        <div>
          <small>Average</small>
          <strong>{formatDuration(job.average_processing_ms)}</strong>
        </div>
      </div>

      <MetadataCounts title="Sources" counts={job.source_counts} />
      <MetadataCounts title="Normalization" counts={job.normalization_counts} />

      <div className="recognition-job-version">
        <span>{job.embedding_model || 'Unknown model'}</span>
        <span>{job.embedding_version || 'Unknown embedding version'}</span>
        <span>{job.preparation_version || 'Unknown preparation version'}</span>
      </div>

      {(job.failure_code || job.failure_detail) && (
        <div className="recognition-job-failure-summary" role="alert">
          <strong title={job.failure_code || undefined}>
            {getEmbeddingJobFailureContext(job.failure_code)}
          </strong>
          {job.failure_detail && <span>{job.failure_detail}</span>}
        </div>
      )}

      <EmbeddingJobActivityPanel job={job} />
    </div>
  );
};

export const EmbeddingJobFailureList = ({
  errors,
}: {
  errors?: EmbeddingJobError[];
}) => {
  if (!errors?.length) {
    return <p className="text-muted">No image-level failures were returned.</p>;
  }

  return (
    <div className="recognition-job-errors">
      {errors.map((error) => (
        <article key={error.id}>
          <div>
            <strong>{getEmbeddingFailureLabel(error.error_code)}</strong>
            {error.error_detail && <span>{error.error_detail}</span>}
          </div>
          <dl>
            <div>
              <dt>Cigar</dt>
              <dd>
                <a
                  href={`/cigars/manager?id=${error.cigar_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {error.cigar_id}
                </a>
              </dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>
                {humanizeKey(error.source_entity_type)} #
                {error.source_record_id}
              </dd>
            </div>
            <div>
              <dt>Attempts</dt>
              <dd>{error.attempt_count}</dd>
            </div>
            <div>
              <dt>Source image ID</dt>
              <dd title={error.source_image_id}>{error.source_image_id}</dd>
            </div>
            {isSafeHttpUrl(error.image_url) && (
              <div>
                <dt>Image</dt>
                <dd>
                  <a
                    href={error.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open image
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </article>
      ))}
    </div>
  );
};

function BulkEmbeddingJobs({
  onCoverageChanged,
}: {
  onCoverageChanged?: () => void;
}) {
  const [jobs, setJobs] = useState<EmbeddingJob[]>([]);
  const [currentJob, setCurrentJob] = useState<EmbeddingJob | null>(null);
  const [detailJob, setDetailJob] = useState<EmbeddingJob | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [limit, setLimit] = useState('');
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [launchingMode, setLaunchingMode] = useState<EmbeddingJobMode | null>(
    null
  );
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  const refreshRequest = useRef(false);
  const cancelRequest = useRef<string | null>(null);
  const pollErrorShown = useRef(false);
  const notifiedTerminalJobs = useRef(new Set<string>());
  const newestJobSnapshots = useRef(new Map<string, EmbeddingJob>());

  const preferNewestJob = useCallback((job: EmbeddingJob) => {
    const currentSnapshot = newestJobSnapshots.current.get(job.id);
    if (currentSnapshot) {
      const currentUpdatedAt = getJobUpdatedAt(currentSnapshot);
      const incomingUpdatedAt = getJobUpdatedAt(job);
      if (incomingUpdatedAt < currentUpdatedAt) return currentSnapshot;
      if (
        incomingUpdatedAt === currentUpdatedAt &&
        !isEmbeddingJobActive(currentSnapshot) &&
        isEmbeddingJobActive(job)
      ) {
        return currentSnapshot;
      }
    }
    newestJobSnapshots.current.set(job.id, job);
    return job;
  }, []);

  const refreshJobs = useCallback(
    async (notifyOnError = true) => {
      if (refreshRequest.current) return;

      try {
        refreshRequest.current = true;
        setIsRefreshing(true);
        const response = await listCigarEmbeddingJobs(RECENT_JOB_LIMIT);
        const recentJobs = (response.jobs || []).map(preferNewestJob);
        setJobs(recentJobs);

        const activeJob = recentJobs.find(isEmbeddingJobActive);
        if (activeJob) {
          try {
            const detail = await getCigarEmbeddingJob(activeJob.id);
            const preferredJob = preferNewestJob(detail.job);
            setCurrentJob(preferredJob);
            setJobs((current) => upsertJob(current, preferredJob));
          } catch (error) {
            setCurrentJob(activeJob);
            if (notifyOnError) {
              Toast.error(
                <ToastContent
                  title="Unable to load active job"
                  message={getApiErrorMessage(
                    error,
                    'The active embedding job details could not be loaded.'
                  )}
                />,
                { position: 'top-right' }
              );
            }
          }
        } else {
          setCurrentJob((current) => {
            if (!current) return null;
            return (
              recentJobs.find((candidate) => candidate.id === current.id) ||
              current
            );
          });
        }
      } catch (error) {
        if (notifyOnError) {
          Toast.error(
            <ToastContent
              title="Unable to load embedding jobs"
              message={getApiErrorMessage(
                error,
                'Recent catalog embedding jobs could not be loaded.'
              )}
            />,
            { position: 'top-right' }
          );
        }
      } finally {
        refreshRequest.current = false;
        setIsLoadingJobs(false);
        setIsRefreshing(false);
      }
    },
    [preferNewestJob]
  );

  useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  const handlePollUpdate = useCallback(
    (job: EmbeddingJob) => {
      const preferredJob = preferNewestJob(job);
      pollErrorShown.current = false;
      setCurrentJob(preferredJob);
      setJobs((current) => upsertJob(current, preferredJob));
      setDetailJob((current) =>
        current?.id === preferredJob.id ? preferredJob : current
      );

      if (
        isEmbeddingJobActive(preferredJob) ||
        notifiedTerminalJobs.current.has(preferredJob.id)
      ) {
        return;
      }
      notifiedTerminalJobs.current.add(preferredJob.id);
      onCoverageChanged?.();

      if (preferredJob.status === 'completed') {
        Toast.success(
          <ToastContent
            title="Bulk embedding job completed"
            message={`${preferredJob.embedded.toLocaleString()} images embedded; ${preferredJob.skipped.toLocaleString()} skipped.`}
          />,
          { position: 'top-right' }
        );
      } else if (preferredJob.status === 'completed_with_errors') {
        Toast.warning(
          <ToastContent
            title="Bulk job completed with errors"
            message={`${preferredJob.failed.toLocaleString()} images failed. Inspect the job to review or retry them.`}
          />,
          { position: 'top-right' }
        );
      } else if (preferredJob.status === 'cancelled') {
        Toast.info(
          <ToastContent
            title="Bulk embedding job cancelled"
            message="Embeddings completed before cancellation were retained."
          />,
          { position: 'top-right' }
        );
      } else if (preferredJob.status === 'failed') {
        Toast.error(
          <ToastContent
            title="Bulk embedding job failed"
            message={
              preferredJob.failure_detail ||
              getEmbeddingJobFailureContext(preferredJob.failure_code)
            }
          />,
          { position: 'top-right' }
        );
      }
    },
    [onCoverageChanged, preferNewestJob]
  );

  const handlePollError = useCallback((error) => {
    if (pollErrorShown.current) return;
    pollErrorShown.current = true;
    Toast.error(
      <ToastContent
        title="Unable to refresh embedding progress"
        message={getApiErrorMessage(
          error,
          'Automatic polling will continue; manual refresh is also available.'
        )}
      />,
      { position: 'top-right' }
    );
  }, []);

  useEmbeddingJobPolling(currentJob, handlePollUpdate, handlePollError);

  const startJob = async (input: StartEmbeddingJobRequest) => {
    if (launchingMode || isEmbeddingJobActive(currentJob)) return;
    const mode = input.mode || 'missing';

    try {
      setLaunchingMode(mode);
      const response = await startCigarEmbeddingJob(input);
      newestJobSnapshots.current.delete(response.job.id);
      notifiedTerminalJobs.current.delete(response.job.id);
      setCurrentJob(response.job);
      setJobs((current) => upsertJob(current, response.job));
      Toast.info(
        <ToastContent
          title={
            response.job.dry_run
              ? 'Discovery job queued'
              : 'Embedding job queued'
          }
          message="The server accepted the asynchronous job. Progress will update automatically."
        />,
        { position: 'top-right' }
      );
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        'The catalog embedding job could not be started.'
      );
      Toast.error(
        <ToastContent
          title="Unable to start embedding job"
          message={message}
        />,
        { position: 'top-right' }
      );
      if (error?.response?.status === 409) {
        await refreshJobs(false);
      }
    } finally {
      setLaunchingMode(null);
    }
  };

  const startMissingJob = () => {
    const parsedLimit = limit.trim() ? Number(limit) : undefined;
    if (
      parsedLimit !== undefined &&
      (!Number.isInteger(parsedLimit) || parsedLimit <= 0)
    ) {
      Toast.error(
        <ToastContent
          title="Invalid batch limit"
          message="Choose All or enter a positive whole number."
        />,
        { position: 'top-right' }
      );
      return;
    }

    startJob({
      mode: 'missing',
      dryRun,
      ...(parsedLimit !== undefined ? { limit: parsedLimit } : {}),
    });
  };

  const confirmFullRegeneration = () => {
    ModalDialog.show({
      title: 'Regenerate the full catalog?',
      type: 'warning',
      message:
        'This reprocesses every eligible catalog image, replaces current embeddings for those sources, and can take significant time. Use it only after an intentional embedding-model or image-preparation change.',
      buttons: [
        { label: 'Cancel', onClick: () => ModalDialog.close() },
        {
          label: 'Regenerate Full Batch',
          color: 'danger',
          onClick: () => {
            ModalDialog.close();
            startJob({
              mode: 'regenerate_all',
              confirmRegenerateAll: true,
            });
          },
        },
      ],
    });
  };

  const refreshCurrentJobDetail = async (jobId: string) => {
    const response = await getCigarEmbeddingJob(jobId);
    const preferredJob = preferNewestJob(response.job);
    setCurrentJob(preferredJob);
    setJobs((current) => upsertJob(current, preferredJob));
    setDetailJob((current) =>
      current?.id === preferredJob.id ? preferredJob : current
    );
    return preferredJob;
  };

  const cancelJob = async (job: EmbeddingJob) => {
    if (!canCancelEmbeddingJob(job) || cancelRequest.current) return;

    try {
      cancelRequest.current = job.id;
      setCancellingJobId(job.id);
      const response = await cancelCigarEmbeddingJob(job.id);
      const cancelledJob = preferNewestJob(response.job);
      notifiedTerminalJobs.current.add(cancelledJob.id);
      setCurrentJob(cancelledJob);
      setJobs((current) => upsertJob(current, cancelledJob));
      setDetailJob((current) =>
        current?.id === cancelledJob.id ? cancelledJob : current
      );
      onCoverageChanged?.();

      Toast.success(
        <ToastContent
          title={
            response.cancelled
              ? 'Embedding job cancelled'
              : 'Embedding job was already cancelled'
          }
          message="Embeddings completed before cancellation were retained."
        />,
        { position: 'top-right' }
      );
      try {
        await refreshCurrentJobDetail(cancelledJob.id);
      } catch (detailError) {
        console.error('Unable to refresh cancelled job activity', detailError);
      }
      await refreshJobs(false);
    } catch (error) {
      const conflictJob =
        error?.response?.status === 409 ? error?.response?.data?.job : null;
      if (conflictJob?.id === job.id) {
        const preferredJob = preferNewestJob(conflictJob);
        setCurrentJob(preferredJob);
        setJobs((current) => upsertJob(current, preferredJob));
        setDetailJob((current) =>
          current?.id === preferredJob.id ? preferredJob : current
        );
      }

      Toast.error(
        <ToastContent
          title="Unable to cancel embedding job"
          message={getApiErrorMessage(
            error,
            'The embedding job could not be cancelled.'
          )}
        />,
        { position: 'top-right' }
      );
      if (error?.response?.status === 409) {
        try {
          await refreshCurrentJobDetail(job.id);
        } catch (detailError) {
          console.error(
            'Unable to refresh conflicted job activity',
            detailError
          );
        }
        await refreshJobs(false);
      }
    } finally {
      cancelRequest.current = null;
      setCancellingJobId(null);
    }
  };

  const confirmCancelJob = (job: EmbeddingJob) => {
    if (!canCancelEmbeddingJob(job) || cancelRequest.current) return;

    ModalDialog.show({
      title: 'Cancel embedding job?',
      type: 'warning',
      message:
        'This stops additional catalog images from being processed. An image already being processed may finish before the job stops. Embeddings completed before cancellation are retained.',
      buttons: [
        { label: 'Keep Running', onClick: () => ModalDialog.close() },
        {
          label: 'Cancel Job',
          color: 'danger',
          onClick: () => {
            ModalDialog.close();
            cancelJob(job);
          },
        },
      ],
    });
  };

  const inspectJob = async (job: EmbeddingJob) => {
    try {
      setLoadingDetailId(job.id);
      const response = await getCigarEmbeddingJob(job.id);
      const preferredJob = preferNewestJob(response.job);
      setDetailJob(preferredJob);
      setJobs((current) => upsertJob(current, preferredJob));
      if (isEmbeddingJobActive(preferredJob)) setCurrentJob(preferredJob);
      setShowDetails(true);
    } catch (error) {
      Toast.error(
        <ToastContent
          title="Unable to inspect embedding job"
          message={getApiErrorMessage(
            error,
            'The embedding job details could not be loaded.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      setLoadingDetailId(null);
    }
  };

  const retryFailures = async (job: EmbeddingJob) => {
    if (!canRetryEmbeddingJob(job) || retryingJobId) return;

    try {
      setRetryingJobId(job.id);
      const response = await retryCigarEmbeddingJobFailures(job.id);
      notifiedTerminalJobs.current.delete(response.job.id);
      if (isEmbeddingJobActive(response.job)) {
        newestJobSnapshots.current.delete(response.job.id);
      }
      const preferredJob = preferNewestJob(response.job);
      setJobs((current) => upsertJob(current, preferredJob));
      setDetailJob((current) =>
        current?.id === preferredJob.id ? preferredJob : current
      );

      if (isEmbeddingJobActive(preferredJob)) {
        setCurrentJob(preferredJob);
        Toast.info(
          <ToastContent
            title="Failed images queued"
            message={`${response.retried.toLocaleString()} failed images were queued for retry.`}
          />,
          { position: 'top-right' }
        );
      } else {
        Toast.info(
          <ToastContent
            title="No retries queued"
            message="The server did not queue any failed images."
          />,
          { position: 'top-right' }
        );
      }
    } catch (error) {
      Toast.error(
        <ToastContent
          title="Unable to retry failed images"
          message={getApiErrorMessage(
            error,
            'The failed images could not be queued for retry.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      setRetryingJobId(null);
    }
  };

  const activeJobKnown =
    isEmbeddingJobActive(currentJob) || jobs.some(isEmbeddingJobActive);
  const launchDisabled = activeJobKnown || Boolean(launchingMode);

  return (
    <Card className="recognition-bulk-card">
      <CardHeader className="recognition-bulk-header">
        <div>
          <h5>Catalog-wide embedding jobs</h5>
          <p>
            Discover and embed approved cigar images and non-pending vitola
            images from the catalog.
          </p>
        </div>
        <Button
          color="light"
          size="sm"
          disabled={isRefreshing}
          onClick={() => refreshJobs()}
        >
          {isRefreshing ? (
            <Spinner size="sm" />
          ) : (
            <Icon name="refresh-cw" size={14} />
          )}
          Refresh
        </Button>
      </CardHeader>

      <div className="recognition-bulk-body">
        <div className="recognition-bulk-launchers">
          <section className="recognition-bulk-routine">
            <div>
              <h6>Generate Missing Embeddings</h6>
              <p>
                Discovers approved cigar images and non-pending vitola images
                from the catalog. Existing current embeddings are skipped.
              </p>
            </div>
            <div className="recognition-bulk-primary-actions">
              <Button
                color="primary"
                disabled={launchDisabled}
                onClick={startMissingJob}
              >
                {launchingMode === 'missing' && <Spinner size="sm" />}
                {dryRun ? 'Run Discovery' : 'Generate Missing Embeddings'}
              </Button>
              <button
                type="button"
                className="recognition-advanced-toggle"
                aria-expanded={showAdvanced}
                onClick={() => setShowAdvanced((current) => !current)}
              >
                Advanced/Test Options
                <Icon
                  name={showAdvanced ? 'chevron-up' : 'chevron-down'}
                  size={14}
                />
              </button>
            </div>

            <Collapse isOpen={showAdvanced}>
              <div className="recognition-bulk-advanced">
                <label className="recognition-discovery-option">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    disabled={launchDisabled}
                    onChange={(event) => setDryRun(event.target.checked)}
                  />
                  <span>
                    <strong>Discovery only</strong>
                    Find eligible sources without generating embeddings.
                  </span>
                </label>
                <label htmlFor="embedding-job-limit">
                  Eligible-source limit
                  <select
                    id="embedding-job-limit"
                    className="form-control"
                    value={limit}
                    disabled={launchDisabled}
                    onChange={(event) => setLimit(event.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="">All (uncapped)</option>
                  </select>
                  <small>All omits the limit from the request.</small>
                </label>
              </div>
            </Collapse>
          </section>

          <section className="recognition-bulk-regenerate">
            <div>
              <h6>Regenerate Full Batch</h6>
              <p>
                Reprocesses every eligible catalog source and replaces its
                current embedding rows. This is expensive and intentionally
                separate from selected-cigar regeneration.
              </p>
            </div>
            <Button
              outline
              color="danger"
              disabled={launchDisabled}
              onClick={confirmFullRegeneration}
            >
              {launchingMode === 'regenerate_all' && <Spinner size="sm" />}
              Regenerate Full Batch
            </Button>
          </section>
        </div>

        {activeJobKnown && (
          <div className="recognition-active-job-notice">
            <Icon name="info" size={15} />
            New catalog-wide jobs are disabled while an active job is known.
          </div>
        )}

        {currentJob && (
          <section className="recognition-bulk-section">
            <div className="recognition-bulk-section-heading">
              <h6>Current job</h6>
              <div>
                <Button
                  color="light"
                  size="sm"
                  disabled={loadingDetailId === currentJob.id}
                  onClick={() => inspectJob(currentJob)}
                >
                  Inspect details
                </Button>
                {canCancelEmbeddingJob(currentJob) && (
                  <Button
                    outline
                    color="danger"
                    size="sm"
                    disabled={cancellingJobId === currentJob.id}
                    onClick={() => confirmCancelJob(currentJob)}
                  >
                    {cancellingJobId === currentJob.id && <Spinner size="sm" />}
                    {cancellingJobId === currentJob.id
                      ? 'Cancelling…'
                      : 'Cancel Job'}
                  </Button>
                )}
                {canRetryEmbeddingJob(currentJob) && (
                  <Button
                    color="warning"
                    size="sm"
                    disabled={retryingJobId === currentJob.id}
                    onClick={() => retryFailures(currentJob)}
                  >
                    Retry Failed Images
                  </Button>
                )}
              </div>
            </div>
            <EmbeddingJobProgress job={currentJob} />
          </section>
        )}

        <section className="recognition-bulk-section">
          <div className="recognition-bulk-section-heading">
            <h6>Recent jobs</h6>
            <span>Newest first · up to {RECENT_JOB_LIMIT}</span>
          </div>
          {isLoadingJobs && (
            <div className="recognition-bulk-loading">
              <Spinner color="primary" size="sm" />
              Loading embedding jobs…
            </div>
          )}
          {!isLoadingJobs && Boolean(jobs.length) && (
            <Table responsive hover size="sm" className="recognition-job-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Discovered</th>
                  <th>Embedded</th>
                  <th>Skipped</th>
                  <th>Failed</th>
                  <th>Progress</th>
                  <th>Model / version</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  return (
                    <tr key={job.id}>
                      <td>
                        {formatDateTime(job.created_at)}
                        {job.started_at && (
                          <small className="d-block text-muted">
                            Started {formatDateTime(job.started_at)}
                          </small>
                        )}
                        {job.completed_at && (
                          <small className="d-block text-muted">
                            Completed {formatDateTime(job.completed_at)}
                          </small>
                        )}
                      </td>
                      <td>
                        <Badge color="info">{getModeLabel(job.mode)}</Badge>
                        {job.dry_run && (
                          <Badge color="info" className="ml-1">
                            Dry run
                          </Badge>
                        )}
                        {job.item_limit !== null && (
                          <small className="d-block text-muted">
                            Limit {job.item_limit}
                          </small>
                        )}
                      </td>
                      <td>
                        <Badge color={getStatusColor(job.status)}>
                          {getEmbeddingJobStatusLabel(job.status)}
                        </Badge>
                      </td>
                      <td>{job.discovered.toLocaleString()}</td>
                      <td>{job.embedded.toLocaleString()}</td>
                      <td>{job.skipped.toLocaleString()}</td>
                      <td className={job.failed ? 'text-danger' : undefined}>
                        {job.failed.toLocaleString()}
                      </td>
                      <td>{getEmbeddingJobProgressText(job)}</td>
                      <td>
                        {job.embedding_model || '—'}
                        <small className="d-block text-muted">
                          {job.embedding_version || '—'}
                        </small>
                      </td>
                      <td>
                        <div className="recognition-job-row-actions">
                          <Button
                            color="light"
                            size="sm"
                            disabled={loadingDetailId === job.id}
                            onClick={() => inspectJob(job)}
                          >
                            {loadingDetailId === job.id ? (
                              <Spinner size="sm" />
                            ) : (
                              'Inspect'
                            )}
                          </Button>
                          {canRetryEmbeddingJob(job) && (
                            <Button
                              color="warning"
                              size="sm"
                              disabled={retryingJobId === job.id}
                              onClick={() => retryFailures(job)}
                            >
                              Retry failures
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {!isLoadingJobs && !jobs.length && (
            <div className="recognition-bulk-empty">
              No catalog-wide embedding jobs have been returned yet.
            </div>
          )}
        </section>
      </div>

      <Modal
        isOpen={showDetails}
        toggle={() => setShowDetails(false)}
        className="modal-dialog-lg recognition-job-details-modal"
      >
        <div className="modal-header">
          <div>
            <h5 className="modal-title">Embedding job details</h5>
            <small className="text-muted">{detailJob?.id}</small>
          </div>
        </div>
        <div className="modal-body">
          {detailJob && <EmbeddingJobProgress job={detailJob} />}
          {detailJob && (
            <EmbeddingRuntimeLogsPanel
              key={detailJob.id}
              job={detailJob}
              enabled={showDetails}
            />
          )}
          {detailJob &&
            (detailJob.failed > 0 || detailJob.errors?.length > 0) && (
              <section className="recognition-job-error-section">
                <h6>Recent image failures</h6>
                <EmbeddingJobFailureList errors={detailJob.errors} />
              </section>
            )}
        </div>
        <div className="modal-footer">
          {detailJob && canRetryEmbeddingJob(detailJob) && (
            <Button
              color="warning"
              disabled={retryingJobId === detailJob.id}
              onClick={() => retryFailures(detailJob)}
            >
              {retryingJobId === detailJob.id && <Spinner size="sm" />}
              Retry Failed Images
            </Button>
          )}
          <Button color="light" onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

export default BulkEmbeddingJobs;
